import { Fragment, useState } from "react";
import { ChevronRight, Keyboard } from "lucide-react";

import { Card, CardBody, CardExtra, CardHead, CardTitle } from "@/components/ui/Card.jsx";
import Modal from "@/components/ui/Modal.jsx";
import { SearchInput } from "@/components/ui/Form.jsx";
import { EmptyState } from "@/components/ui/Display.jsx";
import { TableWrap, Td, Th, Tr } from "@/components/ui/Table.jsx";
import Tag from "@/components/ui/Tag.jsx";
import ProxyImage from "@/components/ProxyImage.jsx";
import { describeKey, formatKeyLogNodes, previewNodes } from "@/lib/keylog.js";
import { formatDateTimeSeconds, formatTime } from "@/lib/format.js";
import { toneOf } from "@/lib/tokens.js";
import { cx } from "@/lib/cx.js";
import { gateMessage } from "@/lib/userDetail.js";

/*
 * Key Log & Clipboard — a port of KeyLogTab.js, which the first pass of this
 * page reduced to a wall of one-key chips.
 *
 * THE FEATURE IS THE RECONSTRUCTION. A key log is only useful as the sentence
 * that was typed; four hundred chips reading "a" "d" "m" "i" "n" are a
 * transcript of the keyboard, not of the work. So a row shows the typed text,
 * with copy/paste surfaced as chips inside it, and the raw entries stay one
 * click away for when the exact stream is what you need.
 *
 * A row is one capture INTERVAL and appears if it has key entries OR clipboard
 * entries — a paste with no typing is still something that happened.
 */
const MODES = [
    { value: "keys", label: "Keystrokes" },
    { value: "clipboard", label: "Clipboard" },
];

const VIEWS = ["formatted", "raw", "clipboard"];

export default function KeyLogList({ shots, mode, onMode, search, onSearch, gates, subject }) {
    const [expanded, setExpanded] = useState(null);
    const [view, setView] = useState({});
    const [image, setImage] = useState(null);

    /*
     * The search is server-side (getUserScreenshots takes titleSearch), so the
     * rows arriving here are already filtered. Filtering again locally would
     * quietly narrow a page the server had already narrowed.
     */
    const rows = shots.filter((s) =>
        mode === "keys" ? s.keyLog.length > 0 : s.clipboard.length > 0,
    );

    const enabled = mode === "keys" ? gates.keyLog : gates.clipboard;
    const term = search.trim().toLowerCase();

    return (
        <>
            <Card>
                <CardHead className="flex-wrap">
                    <CardTitle>Key Log &amp; Clipboard</CardTitle>
                    <CardExtra>
                        <div className="inline-flex gap-0.5 rounded-md bg-slate-200/70 p-0.5 dark:bg-slate-800">
                            {MODES.map((m) => (
                                <button
                                    key={m.value}
                                    type="button"
                                    data-mode={m.value}
                                    aria-pressed={mode === m.value}
                                    onClick={() => onMode(m.value)}
                                    className={
                                        mode === m.value
                                            ? "rounded bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-900 shadow-xs dark:bg-slate-700 dark:text-white"
                                            : "rounded px-2.5 py-1.5 text-xs font-medium text-slate-600 transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                                    }
                                >
                                    {m.label}
                                </button>
                            ))}
                        </div>

                        <SearchInput
                            value={search}
                            onChange={(e) => onSearch(e.target.value)}
                            placeholder="Search window title…"
                            aria-label="Search key log"
                            className="w-56"
                            data-keylog-search
                        />
                    </CardExtra>
                </CardHead>

                {/*
                 * WHAT THIS ACTUALLY RECORDS.
                 *
                 * The previous wording — "key names only, never the typed content" —
                 * was not true, and it was on four pages. The capture is
                 * character-level: the reconstruction below turns a stored log back
                 * into "admin123". Telling people otherwise is a promise the system
                 * does not keep, so the note says what it does.
                 */}
                <div className="flex items-start gap-2.5 border-b border-slate-200 bg-amber-50/60 px-4.5 py-2.5 text-[12px] text-slate-600 dark:border-slate-800 dark:bg-amber-950/20 dark:text-slate-300">
                    <Keyboard className="mt-px size-4 shrink-0 text-amber-500" strokeWidth={2} />
                    <p>
                        Every key pressed is recorded, so this reconstructs what was typed —{" "}
                        <strong className="font-semibold">
                            including anything typed into a password field
                        </strong>
                        . Requires the organization's Key Log setting, or a per-user override, to be
                        enabled.
                    </p>
                </div>

                {rows.length === 0 ? (
                    <CardBody>
                        <EmptyState
                            icon={<Keyboard className="size-9 opacity-50" strokeWidth={1.5} />}
                            title={
                                enabled
                                    ? mode === "keys"
                                        ? "No keystrokes recorded for these screenshots"
                                        : "No clipboard activity recorded for these screenshots"
                                    : gateMessage(mode, gates, { subject: subject || "this user" })
                                          .title
                            }
                            hint={
                                enabled
                                    ? undefined
                                    : gateMessage(mode, gates, { subject: subject || "this user" })
                                          .hint
                            }
                            data-keylog-empty
                        />
                    </CardBody>
                ) : (
                    <TableWrap minWidth="920px">
                        <thead>
                            <tr>
                                <Th className="w-44 pl-4.5">Time</Th>
                                <Th className="w-56">Title / App</Th>
                                <Th className="w-32">Preview</Th>
                                <Th className="w-20 text-center">Keys</Th>
                                <Th className="pr-4.5">Typed</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((s) => {
                                const open = expanded === s.key;
                                const current =
                                    view[s.key] ?? (s.keyLog.length ? "formatted" : "clipboard");

                                return (
                                    <Fragment key={s.key}>
                                        <Tr
                                            data-keylog-row={s.recordId}
                                            onClick={() => setExpanded(open ? null : s.key)}
                                            className="cursor-pointer"
                                        >
                                            <Td className="pl-4.5">
                                                <span className="flex items-center gap-1.5">
                                                    <ChevronRight
                                                        className={cx(
                                                            "size-3.5 shrink-0 text-slate-400 transition-transform",
                                                            open && "rotate-90",
                                                        )}
                                                        strokeWidth={2.2}
                                                    />
                                                    <span className="font-mono text-[12.5px]">
                                                        {formatDateTimeSeconds(s.timestamp)}
                                                    </span>
                                                </span>
                                            </Td>

                                            <Td>
                                                <span className="block max-w-56 truncate text-[12.5px]">
                                                    <Highlight text={s.title || "—"} term={term} />
                                                </span>
                                                <span className="block truncate text-[11.5px] text-slate-400 dark:text-slate-500">
                                                    <Highlight text={s.application} term={term} />
                                                </span>
                                            </Td>

                                            <Td>
                                                <span className="flex flex-wrap gap-1.5">
                                                    {(s.sources ?? [s.src]).map((url, i) => (
                                                        <button
                                                            key={i}
                                                            type="button"
                                                            data-preview={`${s.recordId}#${i}`}
                                                            title={`Display ${i + 1} — click to enlarge`}
                                                            aria-label={`Display ${i + 1}, click to enlarge`}
                                                            /*
                                                             * The row toggles the
                                                             * detail; the thumbnail
                                                             * opens the image. Without
                                                             * stopping propagation the
                                                             * click did both, and the
                                                             * expansion is what you
                                                             * noticed.
                                                             */
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setImage({
                                                                    url,
                                                                    display: i + 1,
                                                                    of: (s.sources ?? [s.src])
                                                                        .length,
                                                                    time: s.timestamp,
                                                                    blurred: s.blurred,
                                                                    title: s.title,
                                                                });
                                                            }}
                                                            className="block shrink-0 rounded border border-slate-200 transition hover:border-brand-400 hover:ring-2 hover:ring-brand-500/20 dark:border-slate-700"
                                                        >
                                                            <ProxyImage
                                                                src={url}
                                                                alt={`Display ${i + 1}`}
                                                                fallbackLabel=""
                                                                className="h-9 w-14 rounded"
                                                                imgClassName={cx(
                                                                    s.blurred && "shot-blurred",
                                                                )}
                                                            />
                                                        </button>
                                                    ))}
                                                </span>
                                            </Td>

                                            <Td className="text-center">
                                                <Tag tone="violet" data-key-count={s.recordId}>
                                                    {s.keyLog.length}
                                                </Tag>
                                            </Td>

                                            <Td className="pr-4.5">
                                                <Preview
                                                    keyLog={s.keyLog}
                                                    clipboard={s.clipboard}
                                                />
                                            </Td>
                                        </Tr>

                                        {open && (
                                            <tr data-keylog-detail={s.recordId}>
                                                <td
                                                    colSpan={5}
                                                    className="bg-slate-50 p-4.5 dark:bg-slate-800/40"
                                                >
                                                    <Detail
                                                        shot={s}
                                                        view={current}
                                                        onView={(v) =>
                                                            setView((prev) => ({
                                                                ...prev,
                                                                [s.key]: v,
                                                            }))
                                                        }
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </TableWrap>
                )}
            </Card>

            {image && <ImageModal image={image} onClose={() => setImage(null)} />}
        </>
    );
}

/*
 * The screenshot behind a row, full size.
 *
 * The old page opened one from every thumbnail and the first port left the
 * thumbnails inert — so a preview you could obviously click did nothing except
 * toggle the row underneath it.
 */
function ImageModal({ image, onClose }) {
    return (
        <Modal
            open
            onClose={onClose}
            size="3xl"
            title={`Display ${image.display}${image.of > 1 ? ` of ${image.of}` : ""}`}
            subtitle={`${formatDateTimeSeconds(image.time)}${image.title ? ` · ${image.title}` : ""}`}
            bodyClassName="p-4 bg-slate-100 dark:bg-slate-950"
        >
            <ProxyImage
                src={image.url}
                alt={`Display ${image.display}`}
                loading="eager"
                fit="contain"
                fallbackLabel="This screenshot is no longer available"
                className="w-full bg-transparent"
                imgClassName={cx("max-h-[72vh] rounded-lg", image.blurred && "shot-blurred")}
                data-modal-image
            />
        </Modal>
    );
}

/* ---- Cells --------------------------------------------------------------- */

function Highlight({ text, term }) {
    if (!term || !text) return text || "—";

    const index = text.toLowerCase().indexOf(term);
    if (index === -1) return text;

    return (
        <>
            {text.slice(0, index)}
            <mark className="bg-amber-200 text-inherit dark:bg-amber-500/40">
                {text.slice(index, index + term.length)}
            </mark>
            {text.slice(index + term.length)}
        </>
    );
}

/** One line of typed text, with shortcuts still visible inside it. */
function Preview({ keyLog, clipboard }) {
    const { nodes, truncated } = previewNodes(keyLog, 120);

    if (!nodes.length) {
        return (
            <span className="text-[12px] text-slate-400 italic dark:text-slate-500">
                {clipboard.length
                    ? `${clipboard.length} clipboard entr${clipboard.length === 1 ? "y" : "ies"}`
                    : "only special keys"}
            </span>
        );
    }

    return (
        <span className="font-mono text-[12px] break-all whitespace-pre-wrap text-slate-600 dark:text-slate-300">
            {nodes.map((n, i) =>
                n.type === "text" ? <span key={i}>{n.value}</span> : <Shortcut key={i} node={n} />,
            )}
            {truncated && <span className="text-brand-600 dark:text-brand-400"> …more</span>}
        </span>
    );
}

function Shortcut({ node }) {
    return (
        <span
            title={`Shortcut: ${node.display}${node.label ? ` (${node.label})` : ""}`}
            className="mx-0.5 inline-block rounded border border-violet-200 bg-violet-50 px-1 align-baseline text-[10.5px] font-semibold text-violet-700 dark:border-violet-800 dark:bg-violet-950/60 dark:text-violet-300"
        >
            {node.display}
            {node.label ? ` ${node.label}` : ""}
        </span>
    );
}

/* ---- Expanded detail ----------------------------------------------------- */

function Detail({ shot, view, onView }) {
    const hasKeys = shot.keyLog.length > 0;
    const hasClipboard = shot.clipboard.length > 0;
    const available = VIEWS.filter((v) => (v === "clipboard" ? hasClipboard : hasKeys));

    const nodes = formatKeyLogNodes(shot.keyLog);

    return (
        <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <p className="text-[11px] font-semibold tracking-wider text-slate-500 uppercase dark:text-slate-400">
                    {shot.keyLog.length} key{shot.keyLog.length === 1 ? "" : "s"}
                    {hasClipboard && `, ${shot.clipboard.length} clipboard`}
                </p>

                <div className="inline-flex gap-0.5 rounded-md bg-slate-200/70 p-0.5 dark:bg-slate-700">
                    {available.map((v) => (
                        <button
                            key={v}
                            type="button"
                            data-view={v}
                            aria-pressed={view === v}
                            onClick={() => onView(v)}
                            className={
                                view === v
                                    ? "rounded bg-white px-2.5 py-1 text-[11.5px] font-semibold text-slate-900 shadow-xs dark:bg-slate-900 dark:text-white"
                                    : "rounded px-2.5 py-1 text-[11.5px] font-medium text-slate-600 transition hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
                            }
                        >
                            {v.charAt(0).toUpperCase() + v.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {view === "formatted" && (
                <div
                    data-formatted
                    className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white p-3 font-mono text-[13px] leading-relaxed break-all whitespace-pre-wrap dark:border-slate-700 dark:bg-slate-900"
                >
                    {nodes.length ? (
                        nodes.map((n, i) =>
                            n.type === "text" ? (
                                <span key={i}>{n.value}</span>
                            ) : (
                                <Shortcut key={i} node={n} />
                            ),
                        )
                    ) : (
                        <span className="text-slate-400 italic dark:text-slate-500">
                            No printable characters — only special keys.
                        </span>
                    )}
                </div>
            )}

            {view === "raw" && (
                <div data-raw className="flex max-h-64 flex-wrap gap-1 overflow-y-auto py-1">
                    {shot.keyLog.map((entry, i) => {
                        const k = describeKey(entry);
                        const tone =
                            k.kind === "shortcut"
                                ? "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-950/60 dark:text-violet-300"
                                : k.kind === "printable"
                                  ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-400"
                                  : k.kind === "modifier"
                                    ? "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/60 dark:text-rose-400"
                                    : "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300";

                        return (
                            <kbd
                                key={i}
                                className={cx(
                                    "rounded border px-1.5 py-0.5 font-mono text-[10.5px]",
                                    tone,
                                )}
                            >
                                {k.display}
                                {k.label ? (
                                    <span className="ml-1 font-normal opacity-75">{k.label}</span>
                                ) : null}
                            </kbd>
                        );
                    })}
                </div>
            )}

            {view === "clipboard" && (
                <div data-clipboard className="space-y-2">
                    {shot.clipboard.map((c, i) => (
                        <div
                            key={i}
                            className="rounded-lg border border-slate-200 bg-white p-2.5 dark:border-slate-700 dark:bg-slate-900"
                        >
                            <div className="mb-1.5 flex items-center gap-1.5">
                                <span
                                    className={cx(
                                        "tag",
                                        toneOf(c.action === "copy" ? "brand" : "emerald").tag,
                                    )}
                                >
                                    {c.action}
                                </span>
                                {c.kind === "image" && (
                                    <span className={cx("tag", toneOf("violet").tag)}>image</span>
                                )}
                                <span className="ml-auto font-mono text-[10.5px] text-slate-400">
                                    {c.timestamp ? formatTime(c.timestamp) : ""}
                                </span>
                            </div>
                            <p className="font-mono text-[11.5px] break-all text-slate-600 dark:text-slate-300">
                                {c.text || (c.kind === "image" ? "(image)" : "")}
                            </p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
